import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  BaseUser,
  ConsentForm,
  DateDocumentElement,
  DateTimeDocumentElement,
  DocumentElementType,
  MultiSelectDocumentElement,
  SignatureDocumentElement,
  SignatureDocumentElementFieldValue,
  SingleCheckboxDocumentElement,
  SingleSelectDocumentElement,
  TextDocumentElement,
  Utils,
} from '@pbt/pbt-ui-components'

import Features from '../../../constants/features'
import { getCurrentClient } from '../../../store/duck/clients'
import { getFeatureToggle } from '../../../store/duck/constants'
import {
  fetchDocumentsListV2,
  getDocumentsV2IsLoading,
  getDocumentsV2List,
  signDocumentV2,
} from '../../../store/duck/documentsV2'
// @ts-ignore
import { getIsCheckInFlow } from '../../../store/duck/flow'
import { getCurrentAppointment } from '../../../store/duck/schedules'
import { PaginatedIdentifiableDocumentInstance } from '../../../types/entities/consentForm'
// @ts-ignore
import { useNavigateWithQueryString } from '../../../utils'
import {
  paginateDocument,
  prepareDocumentForSave,
} from '../../../utils/consentFormUtils'
import useCallbackWhenLoaded from '../../../utils/useCallbackWhenLoaded'
import KioskScreen from '../KioskScreen'

export function ConsentFormScreen() {
  const navigateWithQueryString = useNavigateWithQueryString()
  const dispatch = useDispatch()
  const appointment = useSelector(getCurrentAppointment)
  const client = useSelector(getCurrentClient)
  const clientName = Utils.getPersonString(client as BaseUser)
  const isAutoAddDetailsEnabled = useSelector(
    getFeatureToggle(Features.CONSENT_FORMS_AUTO_ADD_DETAILS),
  )
  const documentsList = useSelector(getDocumentsV2List)
  const [paginatedDocumentsList, setPaginatedDocumentsList] = useState<
    PaginatedIdentifiableDocumentInstance[]
  >([])
  const isLoading = useSelector(getDocumentsV2IsLoading)
  const isCheckInFlow = useSelector(getIsCheckInFlow)

  const appointmentId = appointment && 'id' in appointment ? appointment.id : ''

  const navigateToCompletion = useCallback(() => {
    navigateWithQueryString({
      url: isCheckInFlow ? '/check-in' : '/consent-forms-complete',
    })
  }, [isCheckInFlow, navigateWithQueryString])

  const onDocumentsLoaded = () => {
    const paginatedDocuments = documentsList
      .filter((document) => document.documentElements.length > 0)
      .map((document) => ({
        ...document,
        documentElements: document.documentElements.map((element) =>
          isAutoAddDetailsEnabled &&
          element.type === DocumentElementType.SIGNATURE &&
          !element.signer &&
          clientName
            ? { ...element, signer: clientName }
            : element,
        ),
      }))
      .map((document) => paginateDocument(document))

    if (paginatedDocuments.length === 0) {
      navigateToCompletion()
    } else {
      setPaginatedDocumentsList(paginatedDocuments)
    }
  }

  const callbackWhenLoaded = useCallbackWhenLoaded(
    onDocumentsLoaded,
    getDocumentsV2IsLoading,
  )

  useEffect(() => {
    callbackWhenLoaded()
    dispatch(fetchDocumentsListV2({ appointmentId }))
  }, [appointmentId])

  const handleFormSubmit = (formIndex: number, isFinalPage: boolean) => {
    const document = paginatedDocumentsList[formIndex]

    const signedDocument = prepareDocumentForSave(document)
    dispatch(
      signDocumentV2({
        document: signedDocument,
        generatedFormId: document.id,
      }),
    )

    if (isFinalPage) {
      navigateToCompletion()
    }
  }

  const handleDocumentElementValueChange =
    (formIndex: number, pageIndex: number) =>
    (
      elementIndex: number,
      value:
        | string
        | string[]
        | boolean
        | SignatureDocumentElementFieldValue
        | null,
    ) => {
      const updatedFormsList = [...paginatedDocumentsList]
      const updatedForm = { ...updatedFormsList[formIndex] }
      const updatedPage = [...updatedForm.pages[pageIndex]]
      const updatedElement = { ...updatedPage[elementIndex] }

      switch (updatedElement.type) {
        case DocumentElementType.MULTI_SELECT:
          ;(updatedElement as MultiSelectDocumentElement).selectedOptionIds =
            value as string[]
          break
        case DocumentElementType.SINGLE_SELECT:
          ;(updatedElement as SingleSelectDocumentElement).selectedOptionId =
            value as string | null
          break
        case DocumentElementType.SINGLE_CHECKBOX:
          ;(updatedElement as SingleCheckboxDocumentElement).selected =
            value as boolean
          break
        case DocumentElementType.TEXT_FIELD:
          ;(updatedElement as TextDocumentElement).value = value as string
          break
        case DocumentElementType.DATE:
          ;(updatedElement as DateDocumentElement).date = value as string
          break
        case DocumentElementType.DATE_TIME:
          ;(updatedElement as DateTimeDocumentElement).dateTime =
            value as string
          break
        case DocumentElementType.SIGNATURE: {
          const { signature, signer } =
            value as SignatureDocumentElementFieldValue
          ;(updatedElement as SignatureDocumentElement).signature =
            signature as string
          ;(updatedElement as SignatureDocumentElement).signer =
            signer as string
          break
        }
        default:
          break
      }

      updatedPage[elementIndex] = updatedElement
      updatedForm.pages[pageIndex] = updatedPage
      updatedFormsList[formIndex] = updatedForm
      setPaginatedDocumentsList(updatedFormsList)
    }
  return (
    <KioskScreen proceedButtonLoading={isLoading}>
      {paginatedDocumentsList.length > 0 ? (
        <>
          <ConsentForm
            containerSxProps={{ height: 0 }}
            forms={paginatedDocumentsList}
            readOnly={false}
            onFieldChange={handleDocumentElementValueChange}
            onSubmit={handleFormSubmit}
          />
        </>
      ) : null}
    </KioskScreen>
  )
}
